import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  BuildingOffice2Icon,
  CalendarIcon,
  CameraIcon,
  CheckCircleIcon,
  MapPinIcon,
  ArrowUturnLeftIcon,
  UserIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import {
  useAtualizarCabecalho,
  useAtualizarItem,
  useConcluir,
  useConferencia,
  useReabrir,
} from '../hooks/useConferencias';
import { useEmpresas } from '../hooks/usePesquisas';
import { useToast } from '../components/ui/Toast';
import { useConfirmDialog } from '../components/ui/ConfirmDialog';
import type { CategoriaPendencia, ConferenciaNfeItem, FonteConferencia, StatusConferencia } from '../types/conferencia';
import { aplicarLimiteConferencia, resumoDivergenciaLocal } from '../utils/statusConferencia';
import { extrairMensagemErro } from '../utils/extrairMensagemErro';
import { money, formatDateTime, qtd as fmtQtd } from '../utils/format';
import DivergenciaResumo from '../components/conferencia/DivergenciaResumo';
import ScanBar, { focarScanBar } from '../components/conferencia/ScanBar';
import ItemConferenciaLinha from '../components/conferencia/ItemConferenciaLinha';
import UltimaLeituraCard from '../components/conferencia/UltimaLeituraCard';
import { useIsMobile } from '../hooks/useIsMobile';
import { clampMultiplicador, calcularContagemMultiplicada } from '../utils/multiplicadorCaixa';
import FotosConferenciaDialog from '../components/conferencia/FotosConferenciaDialog';
import ConcluirConferenciaDialog from '../components/conferencia/ConcluirConferenciaDialog';
import AjudaConferenciaDialog from '../components/conferencia/AjudaConferenciaDialog';
import { btnPrimario, btnSecundario, inputIris } from '../components/ui/iris';

const STATUS_CONFERENCIA: Record<StatusConferencia, { label: string; badge: string }> = {
  em_andamento: { label: 'Em andamento', badge: 'bg-accent-soft text-accent-text' },
  concluida: { label: 'Concluída', badge: 'bg-ok-soft text-ok' },
};

const FONTE_CONFERENCIA: Record<FonteConferencia, { label: string; badge: string }> = {
  erp: { label: 'ERP', badge: 'bg-surface-2 text-tx-2' },
  manifesto: { label: 'Manifesto', badge: 'bg-warning-soft text-warning' },
};

/** Duração do destaque visual da linha recém-bipada (ver ItemConferenciaLinha `destacado`). */
const DESTAQUE_MS = 2500;

const ConferenciaWorkspacePage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirmDialog();
  const { id } = useParams<{ id: string }>();
  const conferenciaId = id ? Number(id) : undefined;

  const { data: conferencia, isLoading, error, refetch, isFetching } = useConferencia(conferenciaId);
  const { data: empresasData } = useEmpresas();

  const atualizarItemMut = useAtualizarItem();
  const concluirMut = useConcluir();
  const reabrirMut = useReabrir();
  const atualizarCabecalhoMut = useAtualizarCabecalho();

  // Objeto, não `number`: bipar o MESMO item duas vezes seguidas (caso comum — 12 unidades
  // do mesmo produto) gravaria o mesmo id, o React faria bail-out do setState e o efeito de
  // expiração não reiniciaria — o destaque apagaria no meio da sequência de leituras.
  const [ultimoBipado, setUltimoBipado] = useState<{ id: number } | null>(null);
  // Liga/desliga o FotosConferenciaDialog (Task 16) — o dialog fica montado o tempo todo
  // (isOpen só alterna visibilidade), ver comentário do ciclo de vida das object URLs nele.
  const [fotosAbertas, setFotosAbertas] = useState(false);
  // Liga/desliga o ConcluirConferenciaDialog (C6) — substitui o useConfirmDialog genérico
  // só neste ponto, pra caber a declaração de pendência (categorias + observação).
  const [concluindoAberto, setConcluindoAberto] = useState(false);
  // Liga/desliga o guia de ajuda (botão "?" do cabeçalho).
  const [ajudaAberta, setAjudaAberta] = useState(false);

  // ── Fluxo mobile: câmera controlada aqui (pra orquestrar câmera + card de última leitura) ──
  const isMobile = useIsMobile();
  const [cameraAtiva, setCameraAtiva] = useState(false);
  // Enquanto o operador digita a contagem de um item, o card/câmera colapsam (libera o teclado).
  const [contagemFocada, setContagemFocada] = useState(false);
  // Última leitura por câmera no mobile — alimenta o UltimaLeituraCard. `baseAntes` = contagem
  // do item ANTES da leitura; o total exibido é base + mult × unidadesPorPacote.
  const [ultimaLeitura, setUltimaLeitura] = useState<
    { itemId: number; unidadesPorPacote: number; unidade: string | null; baseAntes: number; mult: number } | null
  >(null);

  // Setor/observação do cabeçalho: estado local (string) que sincroniza do servidor a cada
  // mudança externa e só dispara a mutation no blur/Enter — mesmo molde de
  // ItemConferenciaLinha pra contagem/avaria (evita 1 request por tecla).
  const [setorInput, setSetorInput] = useState('');
  const [observacaoInput, setObservacaoInput] = useState('');

  useEffect(() => {
    setSetorInput(conferencia?.setorRecebimento ?? '');
  }, [conferencia?.setorRecebimento]);

  useEffect(() => {
    setObservacaoInput(conferencia?.observacao ?? '');
  }, [conferencia?.observacao]);

  useEffect(() => {
    if (ultimoBipado === null) return;
    const t = setTimeout(() => setUltimoBipado(null), DESTAQUE_MS);
    return () => clearTimeout(t);
  }, [ultimoBipado]);

  const itens = useMemo(() => conferencia?.itens ?? [], [conferencia?.itens]);
  const resumo = useMemo(() => resumoDivergenciaLocal(itens), [itens]);

  const empresaLabel = useMemo(() => {
    if (!conferencia) return '';
    const e = empresasData?.find((emp) => emp.codigo === String(conferencia.empresa));
    return e ? `${e.codigo} — ${e.nomeFantasia || e.razaosocial}` : `Empresa ${conferencia.empresa}`;
  }, [empresasData, conferencia]);

  const concluida = conferencia?.status === 'concluida';

  /**
   * Grava a contagem de um item, aplicando o teto da nota ANTES de mandar. O servidor reaplica
   * a mesma regra com autoridade (LimiteConferencia) — fazer aqui também é o que dá o feedback
   * instantâneo na doca, sem esperar round-trip. `permitirExcedente` carrega a ORIGEM: bipe
   * (false) que estoura é zerado, digitação confirmada (true) acima da nota vira excedente.
   * Devolve os dois desfechos pra quem chamou escolher a mensagem.
   */
  const persistirItem = (
    item: ConferenciaNfeItem,
    patch: { qtdConferida: number; qtdAvariada: number },
    permitirExcedente: boolean
  ): { zerado: boolean; excedente: boolean } => {
    if (!conferencia) return { zerado: false, excedente: false };

    const limite = aplicarLimiteConferencia(
      item.qtdNota, patch.qtdConferida, patch.qtdAvariada, permitirExcedente);

    atualizarItemMut.mutate(
      {
        conferenciaId: conferencia.id,
        itemId: item.id,
        body: {
          qtdConferida: limite.qtdConferida,
          qtdAvariada: limite.qtdAvariada,
          observacao: item.observacao,
          permitirExcedente,
        },
      },
      {
        onError: () => toast.erro('Falha ao salvar a leitura — verifique a conexão e tente novamente.'),
      }
    );

    return { zerado: limite.zerado, excedente: limite.excedente };
  };

  const posicionarNoItem = (itemId: number) => {
    setUltimoBipado({ id: itemId });
    // Posiciona a linha bipada no centro da tela. Numa nota de 40 itens o produto que
    // acabou de somar pode estar fora do viewport — sem isto o operador biparia às cegas,
    // sem ver a contagem mudar. `scroll-mt-40` na linha compensa a ScanBar sticky.
    requestAnimationFrame(() => {
      document
        .getElementById(`item-conferencia-${itemId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  // ── Card "última leitura" (mobile): `−/+` e chips gravam NA HORA e de forma SÍNCRONA ──
  // Igual ao stepper manual da ItemConferenciaLinha (1 PUT por toque, sem debounce). É
  // deliberadamente SEM diálogo de excedente: um `await confirm` aqui rodaria FORA da
  // serialização da câmera (bipandoRef só cobre o bipe) — com o diálogo aberto, um novo bipe
  // trocaria a `ultimaLeitura` sob os pés deste handler. O card é ajuste deliberado por toque
  // discreto, então passar da nota grava direto como excedente (o número fica âmbar e entra na
  // divergência); a proteção contra "bipar a caixa achando que era unidade" fica no BIPE
  // (handleBipar, que segue confirmando e é serializado). Síncrono ⇒ zero janela de interleaving.
  const onMultUltima = (novoMult: number) => {
    const alvo = ultimaLeitura;
    if (!alvo) return;
    const item = itens.find((i) => i.id === alvo.itemId);
    if (!item) return;
    const m = clampMultiplicador(novoMult);
    const total = calcularContagemMultiplicada(alvo.baseAntes, m, alvo.unidadesPorPacote);
    const excede = total + item.qtdAvariada > item.qtdNota;
    setUltimaLeitura({ ...alvo, mult: m });
    persistirItem(item, { qtdConferida: total, qtdAvariada: item.qtdAvariada }, excede);
  };

  const handleBipar = async (item: ConferenciaNfeItem, unidades: number, unidade: string | null) => {
    const novaQtd = item.qtdConferida + unidades;
    const baseAntes = item.qtdConferida; // contagem ANTES desta leitura — base do card
    posicionarNoItem(item.id);

    // Registra a última leitura no card SÓ quando a leitura é efetivamente gravada — nunca num
    // bipe que o operador cancelou (senão o card exibiria um item que não foi contado).
    const registrarUltima = () => {
      if (isMobile) setUltimaLeitura({ itemId: item.id, unidadesPorPacote: unidades, unidade, baseAntes, mult: 1 });
    };

    // Estourar a nota por bipagem PERGUNTA (igual à digitação) em vez de zerar: o operador vê
    // "a nota tem 60, esta leitura levaria a 67" e decide registrar o excedente ou não. Cancelar
    // mantém a contagem como estava (nada é gravado) — protege contra bipar a caixa achando que
    // era unidade sem destruir o que já foi contado.
    if (novaQtd + item.qtdAvariada > item.qtdNota) {
      const ok = await confirm({
        titulo: 'Registrar acima da nota',
        mensagem: `A nota tem ${fmtQtd(item.qtdNota)}${item.unidade ? ` ${item.unidade}` : ''} de "${item.descricao ?? 'item'}", e esta leitura levaria a ${fmtQtd(novaQtd)}. Registrar o excedente?`,
        textoConfirmar: 'Registrar excedente',
      });
      focarScanBar(); // o dialog roubou o foco do leitor — devolve em qualquer desfecho
      if (!ok) {
        toast.aviso(`Leitura não registrada — "${item.descricao ?? 'item'}" mantido em ${fmtQtd(item.qtdConferida)}.`);
        return false; // cancelou o excedente: nada gravado — a ScanBar não queima o multiplicador
      }
      persistirItem(item, { qtdConferida: novaQtd, qtdAvariada: item.qtdAvariada }, true);
      toast.aviso(`"${item.descricao ?? 'Item'}" registrado acima da nota — confira a divergência.`);
      registrarUltima();
      return true;
    }

    persistirItem(item, { qtdConferida: novaQtd, qtdAvariada: item.qtdAvariada }, false);
    // Confirma o que a leitura somou: com embalagens diferentes na mesma família, "+12 un
    // (CX)" é a única forma de o operador perceber na hora que bipou a caixa e não a unidade.
    toast.sucesso(`+${fmtQtd(unidades)} ${unidade ? `un (${unidade})` : 'un'} · ${item.descricao ?? ''}`.trim());
    registrarUltima();
    return true;
  };

  // Contagem/avaria MANUAL. `permitirExcedente` vem da linha (ItemConferenciaLinha), que já
  // confirmou com o operador quando o número passava da nota — aqui só persiste e avisa.
  // O aviso descreve o FATO ("acima da nota"), não o status: com avaria > 0 o item fica com
  // badge "Avaria" (avaria prevalece em StatusItemConferencia), então cravar "excedente" no
  // texto contradiria o badge. Quem diz o status exato é o badge.
  const avisarAcimaDaNota = (item: ConferenciaNfeItem, excedente: boolean) => {
    if (excedente) toast.aviso(`"${item.descricao ?? 'Item'}" registrado acima da quantidade da nota — confira a divergência.`);
  };

  const handleAlterarContagem = (item: ConferenciaNfeItem, novaQtd: number, permitirExcedente: boolean) => {
    const { excedente } = persistirItem(item, { qtdConferida: novaQtd, qtdAvariada: item.qtdAvariada }, permitirExcedente);
    avisarAcimaDaNota(item, excedente);
    // Contagem editada à mão fora do card: o card projeta de um `baseAntes` congelado no bipe,
    // então ficaria stale e um toque nele sobrescreveria este ajuste manual. Dispensa o card do
    // item — reaparece no próximo bipe, já com a base atualizada.
    if (isMobile && ultimaLeitura?.itemId === item.id) setUltimaLeitura(null);
  };

  const handleAlterarAvaria = (item: ConferenciaNfeItem, novaQtdAvariada: number, permitirExcedente: boolean) => {
    const { excedente } = persistirItem(item, { qtdConferida: item.qtdConferida, qtdAvariada: novaQtdAvariada }, permitirExcedente);
    avisarAcimaDaNota(item, excedente);
  };

  // Update PARCIAL: cada commit manda só o campo que editou (`null` no outro = "não mexe",
  // ver AtualizarCabecalhoAsync no backend). String vazia continua um "limpar" legítimo —
  // por isso `valor` vai como está (sem `|| null`). Sem isso, dois commits em corrida (edita
  // setor, tabula pra observação, digita e sai antes do 1º PUT voltar) fariam o 2º PUT levar
  // o valor PRÉ-edição do outro campo (lido do cache do react-query, que só invalida — não
  // tem patch otimista) e reverter a mudança que acabou de salvar, em silêncio.
  const commitSetor = () => {
    if (!conferencia) return;
    const valor = setorInput.trim();
    const atual = conferencia.setorRecebimento ?? '';
    if (valor === atual) return;
    atualizarCabecalhoMut.mutate(
      { id: conferencia.id, setorRecebimento: valor, observacao: null },
      { onError: () => { toast.erro('Falha ao salvar o setor de recebimento.'); setSetorInput(atual); } }
    );
  };

  const commitObservacao = () => {
    if (!conferencia) return;
    const valor = observacaoInput.trim();
    const atual = conferencia.observacao ?? '';
    if (valor === atual) return;
    atualizarCabecalhoMut.mutate(
      { id: conferencia.id, setorRecebimento: null, observacao: valor },
      { onError: () => { toast.erro('Falha ao salvar a observação.'); setObservacaoInput(atual); } }
    );
  };

  const blurCampoOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') e.currentTarget.blur();
  };

  const abrirConcluir = () => setConcluindoAberto(true);

  // Substitui o antigo useConfirmDialog genérico neste ponto: o ConcluirConferenciaDialog
  // já mostra o resumo de divergência e coleta a declaração de pendência (categorias +
  // observação) — concluir com 0 categorias marcadas é a mesma conclusão de sempre.
  const handleConcluir = (categorias: CategoriaPendencia[], observacao: string) => {
    if (!conferencia) return;
    concluirMut.mutate(
      { id: conferencia.id, body: { pendenciaCategorias: categorias, pendenciaObservacao: observacao || null } },
      {
        onSuccess: () => {
          setConcluindoAberto(false);
          toast.sucesso(categorias.length > 0 ? 'Conferência concluída com pendência.' : 'Conferência concluída.');
          navigate('/conferencia');
        },
        onError: () => toast.erro('Falha ao concluir a conferência.'),
      }
    );
  };

  const handleReabrir = async () => {
    if (!conferencia) return;
    const ok = await confirm({
      titulo: 'Reabrir conferência',
      mensagem: 'A conferência volta para "em andamento" e aceita novas leituras.',
      textoConfirmar: 'Reabrir',
    });
    if (!ok) return;

    reabrirMut.mutate(conferencia.id, {
      onSuccess: () => toast.sucesso('Conferência reaberta.'),
      onError: (error) => toast.erro(extrairMensagemErro(error, 'Falha ao reabrir a conferência.')),
    });
  };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-[1280px] space-y-4">
        <p className="text-sm text-tx-3">Carregando conferência…</p>
      </div>
    );
  }

  if (error || !conferencia) {
    return (
      <div className="mx-auto max-w-[1280px] space-y-3">
        <Link to="/conferencia" className="inline-flex items-center gap-1 text-sm font-medium text-tx-3 hover:text-tx-2">
          <ArrowLeftIcon className="h-4 w-4" /> Conferências
        </Link>
        <div className="rounded-iris-lg border border-danger-border bg-danger-soft p-4">
          <h3 className="text-sm font-semibold text-danger">Erro ao carregar a conferência</h3>
          <p className="mt-1 text-sm text-tx-2">
            {error ? 'Não foi possível carregar esta conferência.' : 'Conferência não encontrada.'}
          </p>
        </div>
      </div>
    );
  }

  const statusInfo = STATUS_CONFERENCIA[conferencia.status];
  const fonteInfo = FONTE_CONFERENCIA[conferencia.fonte];

  return (
    <div className="mx-auto max-w-[1280px] space-y-3">
      {/* Header da NF-e */}
      <header className="rounded-iris-lg border border-line bg-surface p-3 sm:p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <Link to="/conferencia" className="inline-flex items-center gap-1 text-xs font-medium text-tx-3 hover:text-tx-2">
                <ArrowLeftIcon className="h-3.5 w-3.5" /> Conferências
              </Link>
              <div className="flex flex-shrink-0 items-center gap-0.5">
                <button
                  type="button"
                  onClick={() => setAjudaAberta(true)}
                  className="inline-flex items-center justify-center rounded-iris p-2.5 text-tx-3 transition-colors hover:bg-surface-2 md:p-1"
                  title="Como usar a conferência"
                  aria-label="Ajuda"
                >
                  <QuestionMarkCircleIcon className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => refetch()}
                  disabled={isFetching}
                  className="inline-flex items-center justify-center rounded-iris p-2.5 text-tx-3 transition-colors hover:bg-surface-2 disabled:opacity-50 md:p-1"
                  title="Atualizar"
                >
                  <ArrowPathIcon className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
            <h1 className="mt-1 flex flex-wrap items-center gap-2 text-lg font-semibold text-tx sm:text-xl">
              NF-e {conferencia.numeroNfe ?? '—'}
              {conferencia.serieNfe && <span className="text-tx-3">/{conferencia.serieNfe}</span>}
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${fonteInfo.badge}`}>{fonteInfo.label}</span>
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${statusInfo.badge}`}>{statusInfo.label}</span>
            </h1>
            <p className="mt-0.5 truncate text-sm text-tx-2">{conferencia.fornecedorNome || 'Fornecedor não informado'}</p>
          </div>
          <div className="flex-shrink-0 text-right">
            <div className="text-[10px] uppercase tracking-wide text-tx-3">Valor NF</div>
            <div className="text-lg font-bold tabular-nums text-tx sm:text-xl">
              {conferencia.valorTotalNf != null ? money(conferencia.valorTotalNf) : '—'}
            </div>
          </div>
        </div>

        <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 border-t border-line pt-3 text-[13px] sm:grid-cols-4">
          <div className="flex items-center gap-1.5 text-tx-2">
            <BuildingOffice2Icon className="h-3.5 w-3.5 flex-shrink-0 text-tx-3" />
            <span className="truncate" title={empresaLabel}>{empresaLabel}</span>
          </div>
          <div className="flex items-center gap-1.5 text-tx-2">
            <MapPinIcon className="h-3.5 w-3.5 flex-shrink-0 text-tx-3" />
            <input
              type="text"
              value={setorInput}
              onChange={(e) => setSetorInput(e.target.value)}
              onBlur={commitSetor}
              onKeyDown={blurCampoOnEnter}
              disabled={concluida}
              placeholder="Setor de recebimento"
              aria-label="Setor de recebimento"
              maxLength={60}
              className={`${inputIris} h-11 min-w-0 flex-1 px-2 text-[13px] md:h-7`}
            />
          </div>
          <div className="flex items-center gap-1.5 text-tx-2">
            <CalendarIcon className="h-3.5 w-3.5 flex-shrink-0 text-tx-3" />
            <span className="tabular-nums">{formatDateTime(conferencia.criadoEm)}</span>
          </div>
          <div className="flex items-center gap-1.5 text-tx-2">
            <UserIcon className="h-3.5 w-3.5 flex-shrink-0 text-tx-3" />
            <span className="truncate">{conferencia.usuario || '—'}</span>
          </div>
        </dl>
      </header>

      {/* ScanBar + divergência ao vivo — sticky, sempre alcançável no scroll (`<main>` é o
          scroll container real, ver Layout.tsx; `top-0` gruda no topo da área visível). */}
      <div className="sticky top-0 z-20 space-y-2.5 rounded-iris-lg border border-line bg-surface p-3 shadow-iris-xl sm:p-4">
        <ScanBar
          itens={itens}
          disabled={concluida}
          onBipar={handleBipar}
          multiplicadorAtivo={conferencia.multiplicadorAtivo}
          isMobile={isMobile}
          cameraAtiva={cameraAtiva}
          onCameraChange={(v) => { setCameraAtiva(v); if (!v) setUltimaLeitura(null); }}
        />
        {isMobile && cameraAtiva && !contagemFocada && ultimaLeitura && (() => {
          const it = itens.find((i) => i.id === ultimaLeitura.itemId);
          return it ? (
            <UltimaLeituraCard
              item={it}
              unidadesPorPacote={ultimaLeitura.unidadesPorPacote}
              unidade={ultimaLeitura.unidade}
              baseAntes={ultimaLeitura.baseAntes}
              mult={ultimaLeitura.mult}
              mostrarChips={conferencia.multiplicadorAtivo}
              onMult={onMultUltima}
            />
          ) : null;
        })()}
        <DivergenciaResumo resumo={resumo} />
      </div>

      {/* Checklist de itens */}
      <div className="overflow-hidden rounded-iris-lg border border-line bg-surface">
        <div className="hidden border-b border-line bg-surface-2 px-4 py-2 md:block">
          <div className="grid grid-cols-12 gap-2 text-[11px] font-medium uppercase tracking-wide text-tx-3">
            <div className="col-span-5">Produto</div>
            <div className="col-span-2">Nota</div>
            <div className="col-span-2">Contagem</div>
            <div className="col-span-1">Avaria</div>
            <div className="col-span-2">Status</div>
          </div>
        </div>

        {itens.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-tx-3">Esta NF-e não tem itens.</div>
        ) : (
          <div className="divide-y divide-line">
            {itens.map((item) => (
              <ItemConferenciaLinha
                key={item.id}
                item={item}
                destacado={item.id === ultimoBipado?.id}
                bloqueado={concluida}
                permiteContagemManual={conferencia.contagemManualPermitida}
                onAlterarContagem={(novaQtd, permitirExcedente) => handleAlterarContagem(item, novaQtd, permitirExcedente)}
                onAlterarAvaria={(novaQtdAvariada, permitirExcedente) => handleAlterarAvaria(item, novaQtdAvariada, permitirExcedente)}
                onFocoContagem={isMobile ? setContagemFocada : undefined}
              />
            ))}
          </div>
        )}
      </div>

      {/* Rodapé: fotos + concluir/reabrir + observação */}
      <div className="rounded-iris-lg border border-line bg-surface p-3 sm:p-4">
        <div className="flex flex-col-reverse gap-2.5 sm:flex-row sm:items-center sm:justify-between">
          <button type="button" onClick={() => setFotosAbertas(true)} className={`${btnSecundario} w-full sm:w-auto`}>
            <CameraIcon className="h-4 w-4" />
            Fotos
          </button>

          {concluida ? (
            <button type="button" onClick={handleReabrir} disabled={reabrirMut.isPending} className={`${btnSecundario} w-full sm:w-auto`}>
              <ArrowUturnLeftIcon className="h-4 w-4" />
              {reabrirMut.isPending ? 'Reabrindo…' : 'Reabrir'}
            </button>
          ) : (
            <button type="button" onClick={abrirConcluir} disabled={concluirMut.isPending} className={`${btnPrimario} w-full sm:w-auto`}>
              <CheckCircleIcon className="h-4 w-4" />
              {concluirMut.isPending ? 'Concluindo…' : 'Concluir conferência'}
            </button>
          )}
        </div>

        <div className="mt-3 border-t border-line pt-3">
          <label htmlFor="observacao-conferencia" className="text-[10px] font-medium uppercase tracking-wide text-tx-3">
            Observação da conferência
          </label>
          <textarea
            id="observacao-conferencia"
            value={observacaoInput}
            onChange={(e) => setObservacaoInput(e.target.value)}
            onBlur={commitObservacao}
            disabled={concluida}
            placeholder="Nenhuma observação registrada."
            rows={2}
            maxLength={500}
            className="mt-1 w-full resize-none rounded-iris-sm border border-line-strong bg-surface-2 px-3 py-2 text-sm text-tx placeholder:text-tx-3 focus:border-accent focus:bg-surface focus:outline-none focus:ring-[3px] focus:ring-accent-soft disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>
      </div>

      <FotosConferenciaDialog
        conferenciaId={conferencia.id}
        isOpen={fotosAbertas}
        // Mesmo motivo do fechamento da foto ampliada: qualquer overlay que rouba o foco
        // precisa devolvê-lo, senão o bipe seguinte é digitado no body e some em silêncio.
        onClose={() => { setFotosAbertas(false); focarScanBar(); }}
        somenteLeitura={concluida}
      />

      <ConcluirConferenciaDialog
        isOpen={concluindoAberto}
        resumo={resumo}
        processando={concluirMut.isPending}
        onCancelar={() => setConcluindoAberto(false)}
        onConcluir={handleConcluir}
      />

      <AjudaConferenciaDialog
        aberto={ajudaAberta}
        onFechar={() => { setAjudaAberta(false); focarScanBar(); }}
        multiplicadorAtivo={conferencia.multiplicadorAtivo}
      />
    </div>
  );
};

export default ConferenciaWorkspacePage;
